import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../data";
import { PillView, useAction } from "../../components/ui";
import { date, time } from "../../lib/format";
import { Fehler, Laden } from "./parts";
import { kategorieLabel, meldungStatusPill, ticketNummer, ticketStatusPill, groesse } from "../agentur/hilfe/modell";
import type { Anhang, TicketNachricht, TicketEreignis } from "../agentur/hilfe/data";
type Nachricht = TicketNachricht & { draft?: boolean };

/**
 * Betreiber-Konsole (0176): gemeldete Fehler (gruppiert nach Fingerabdruck, mit Spur und Bild der Seite) und Tickets der
 * Agenturen (Antworten, interne Notizen, Status, Priorität, Zuweisung, Antwortvorlagen). Nur deutsch wie die übrige Konsole.
 */

interface Meldung {
  id: string; org_id: string | null; org_name: string | null; user_email: string | null; portal: string; role: string | null; page: string | null;
  message: string; user_note: string | null; release: string | null; browser: string | null; fingerprint: string; status: string; notes: string | null;
  resolved_release: string | null; ticket_id: string | null; has_screenshot: boolean; created_at: string;
}
interface TicketZeile {
  id: string; number: number; org_id: string; org_name: string; category: string; subject: string; status: string; priority: string;
  assignee: string | null; assignee_email: string | null; created_by_email: string | null; error_report_id: string | null;
  created_at: string; updated_at: string; last_customer_at: string | null; last_support_at: string | null; messages: number;
}
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;
const tt = (d: string, _e: string) => d;
/** Status aus Sicht des Betriebs (die Agentur liest „Antwort von uns“, wir „wartet auf Kunde“) */
const BETRIEB_STATUS: Record<string, string> = { offen: "offen", in_bearbeitung: "in Bearbeitung", wartet_auf_kunde: "wartet auf Kunde", geloest: "gelöst", geschlossen: "geschlossen" };
const betriebPill = (s: string) => ({ ...ticketStatusPill(s, tt), label: BETRIEB_STATUS[s] ?? s });

// ── Gemeldete Fehler ─────────────────────────────────────────────────────────
export function Fehlermeldungen() {
  const [tage, setTage] = useState(30);
  const [status, setStatus] = useState("offen");
  const [portal, setPortal] = useState("");
  const q = useQuery({ queryKey: ["platform_error_reports", tage], queryFn: async () => parse<Meldung[]>(await api.read("platform_error_reports", { p_days: tage })) });
  const gruppen = useMemo(() => {
    const m = new Map<string, Meldung[]>();
    for (const r of q.data ?? []) {
      if (status === "offen" && !(r.status === "neu" || r.status === "in_arbeit")) continue;
      if (status !== "offen" && status !== "" && r.status !== status) continue;
      if (portal && r.portal !== portal) continue;
      m.set(r.fingerprint, [...(m.get(r.fingerprint) ?? []), r]);
    }
    return [...m.values()].sort((a, b) => b.length - a.length || b[0]!.created_at.localeCompare(a[0]!.created_at));
  }, [q.data, status, portal]);
  return (
    <>
      <title>Gemeldete Fehler · Plattform · CreatorOffice</title>
      <h1>Gemeldete Fehler</h1>
      <div className="row tk-filter">
        <label>Status<select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="offen">offen (neu, in Arbeit)</option><option value="">alle</option><option value="neu">neu</option>
          <option value="in_arbeit">in Arbeit</option><option value="erledigt">erledigt</option><option value="kein_fehler">kein Fehler</option>
        </select></label>
        <label>Bereich<select value={portal} onChange={(e) => setPortal(e.target.value)}>
          <option value="">alle</option><option value="agentur">Agentur</option><option value="creator">Creator</option><option value="einrichtung">Einrichtung</option><option value="konto">Konto</option><option value="betrieb">Betrieb</option>
        </select></label>
        <label>Zeitraum<select value={tage} onChange={(e) => setTage(Number(e.target.value))}>
          <option value={7}>7 Tage</option><option value={30}>30 Tage</option><option value={90}>90 Tage</option><option value={180}>180 Tage</option>
        </select></label>
      </div>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : q.isPending ? <Laden /> : gruppen.length === 0 ? (
        <div className="panel tk-empty"><p>Keine gemeldeten Fehler in dieser Auswahl.</p></div>
      ) : (
        <div className="tablewrap">
          <table>
            <caption className="sr-only">Gemeldete Fehler, gleiche zusammengefasst</caption>
            <thead><tr><th scope="col">Meldung</th><th scope="col">Anzahl</th><th scope="col">Agenturen</th><th scope="col">Zuletzt</th><th scope="col">Status</th></tr></thead>
            <tbody>
              {gruppen.map((g) => (
                <tr key={g[0]!.fingerprint}>
                  <td><Link to={`/betrieb/fehler/${g[0]!.id}`}>{g[0]!.message}</Link><small>{g[0]!.portal} · {g[0]!.page ?? "–"}</small></td>
                  <td className="num">{g.length}</td>
                  <td>{[...new Set(g.map((x) => x.org_name ?? "ohne Agentur"))].slice(0, 3).join(", ")}</td>
                  <td>{date(g[0]!.created_at)} {time(g[0]!.created_at)}</td>
                  <td><PillView pill={meldungStatusPill(g[0]!.status, tt)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

interface MeldungVoll extends Omit<Meldung, "org_name" | "user_email" | "has_screenshot"> {
  context: Record<string, string>; trail: { t: string; at: string; text: string }[]; tech: { t: string; at: string; text: string; stack?: string; status?: number }[];
  screenshot: string | null; user_id: string | null;
}
export function FehlermeldungDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["error_report_full", id], queryFn: async () => (await api.select<MeldungVoll>("error_reports", { filters: [["id", "eq", id]] }))[0] ?? null });
  const gleiche = useQuery({ queryKey: ["error_report_same", q.data?.fingerprint], enabled: !!q.data, queryFn: () => api.select<{ id: string }>("error_reports", { columns: "id", filters: [["fingerprint", "eq", q.data!.fingerprint]] }) });
  const [notiz, setNotiz] = useState<string | null>(null);
  const [version, setVersion] = useState("");
  const { busy, error, run } = useAction();
  if (q.error) return <Fehler error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Laden />;
  const r = q.data;
  if (!r) return <div className="panel tk-empty"><p>Meldung nicht gefunden (nach 180 Tagen gelöscht?).</p><Link className="btn" to="/betrieb/fehler">Zur Liste</Link></div>;
  const setzen = (status: string, allen: boolean) => run(status, () => api.rpc("platform_error_report_update", {
    p_id: r.id, p_status: status, p_notes: notiz ?? r.notes, p_release: version || null, p_all_same: allen,
  }), status === "erledigt" && version ? "Erledigt; die meldenden Personen bekommen eine Mail." : "Status gespeichert.");
  return (
    <>
      <title>Gemeldeter Fehler · Plattform · CreatorOffice</title>
      <h1>{r.message}</h1>
      <div className="grid">
        <section className="panel stack">
          <h2>Meldung</h2>
          <p>
            <PillView pill={meldungStatusPill(r.status, tt)} />{r.resolved_release ? ` · behoben in ${r.resolved_release}` : ""}<br />
            {date(r.created_at)} {time(r.created_at)} · {r.portal}{r.role ? ` (${r.role})` : ""} · {r.page ?? "–"}<br />
            Version {r.release ?? "–"} · {r.browser ?? "–"}<br />
            Gleiche Meldungen: {gleiche.data?.length ?? "…"}
          </p>
          {r.user_note && <blockquote className="tk-melden-zitat">{r.user_note}</blockquote>}
          {r.org_id && <Link to={`/betrieb/agenturen/${r.org_id}`}>Zur Agentur</Link>}
          {r.ticket_id ? <Link to={`/betrieb/tickets/${r.ticket_id}`}>Zum Ticket</Link> : r.org_id && (
            <button type="button" disabled={!!busy} onClick={() => void run("ticket", () => api.rpc<string>("platform_error_to_ticket", { p_id: r.id }), "Ticket angelegt.").then((tid) => tid && navigate(`/betrieb/tickets/${tid}`))}>In Ticket umwandeln</button>
          )}
        </section>
        <section className="panel stack">
          <h2>Bearbeiten</h2>
          <label>Interne Notiz<textarea rows={3} value={notiz ?? r.notes ?? ""} onChange={(e) => setNotiz(e.target.value)} /></label>
          <label>Behoben in Version (für die Mail an die meldende Person)<input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="z. B. 2026-10-07" /></label>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button type="button" disabled={!!busy} onClick={() => void setzen("in_arbeit", true)}>In Arbeit (alle gleichen)</button>
            <button type="button" className="primary" disabled={!!busy} onClick={() => void setzen("erledigt", true)}>Erledigt (alle gleichen)</button>
            <button type="button" disabled={!!busy} onClick={() => void setzen("kein_fehler", false)}>Kein Fehler</button>
          </div>
        </section>
      </div>
      {r.screenshot && (
        <section className="panel stack"><h2>Bild der Seite</h2><img className="tk-melden-gross" src={r.screenshot} alt="Bild der Seite zum Zeitpunkt der Meldung, Eingaben geschwärzt" /></section>
      )}
      <section className="panel stack">
        <h2>Letzte Schritte</h2>
        <ol className="tk-spur">{r.trail.map((s, i) => <li key={i}><small>{time(s.at)}</small> {s.t}: {s.text}</li>)}</ol>
        <h2>Technische Spuren</h2>
        {r.tech.length === 0 ? <p>Keine.</p> : (
          <ol className="tk-spur">{r.tech.map((s, i) => (
            <li key={i}><small>{time(s.at)}</small> {s.t}{s.status !== undefined ? ` (${s.status})` : ""}: {s.text}{s.stack && <details><summary>Stack</summary><pre className="mono">{s.stack}</pre></details>}</li>
          ))}</ol>
        )}
        <h2>Bezug</h2>
        <pre className="mono">{JSON.stringify(r.context, null, 2)}</pre>
      </section>
    </>
  );
}

// ── Tickets ──────────────────────────────────────────────────────────────────
export function Tickets() {
  const [status, setStatus] = useState("offen");
  const q = useQuery({ queryKey: ["platform_support_tickets"], queryFn: async () => parse<TicketZeile[]>(await api.read("platform_support_tickets", { p_days: 365 })) });
  const liste = (q.data ?? []).filter((x) => status === "" || (status === "offen" ? ["offen", "in_bearbeitung"].includes(x.status) : x.status === status));
  const prio: Record<string, number> = { dringend: 0, hoch: 1, normal: 2, niedrig: 3 };
  liste.sort((a, b) => (prio[a.priority] ?? 2) - (prio[b.priority] ?? 2) || (a.last_customer_at ?? a.created_at).localeCompare(b.last_customer_at ?? b.created_at));
  return (
    <>
      <title>Tickets · Plattform · CreatorOffice</title>
      <h1>Tickets</h1>
      <div className="row tk-filter">
        <label>Status<select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="offen">zu bearbeiten (offen, in Bearbeitung)</option><option value="wartet_auf_kunde">wartet auf Kunde</option>
          <option value="geloest">gelöst</option><option value="geschlossen">geschlossen</option><option value="">alle</option>
        </select></label>
      </div>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : q.isPending ? <Laden /> : liste.length === 0 ? (
        <div className="panel tk-empty"><p>Keine Tickets in dieser Auswahl.</p></div>
      ) : (
        <div className="tablewrap">
          <table>
            <caption className="sr-only">Tickets der Agenturen</caption>
            <thead><tr><th scope="col">Ticket</th><th scope="col">Agentur</th><th scope="col">Kategorie</th><th scope="col">Priorität</th><th scope="col">Status</th><th scope="col">Letzte Kundenantwort</th></tr></thead>
            <tbody>
              {liste.map((x) => (
                <tr key={x.id}>
                  <td><Link to={`/betrieb/tickets/${x.id}`}>{ticketNummer(x.number)} {x.subject}</Link><small>{x.created_by_email ?? "–"} · {x.messages} Nachrichten</small></td>
                  <td>{x.org_name}</td>
                  <td>{kategorieLabel(x.category, tt)}</td>
                  <td>{x.priority}</td>
                  <td><PillView pill={betriebPill(x.status)} /></td>
                  <td>{x.last_customer_at ? `${date(x.last_customer_at)} ${time(x.last_customer_at)}` : "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function AnhangLink({ a }: { a: Anhang }) {
  return (
    <button type="button" className="quiet" onClick={() => void api.download("support", a.path).then((b) => { const u = URL.createObjectURL(b); window.open(u, "_blank", "noopener"); setTimeout(() => URL.revokeObjectURL(u), 60_000); })}>
      {a.name} <small>{groesse(a.size)}</small>
    </button>
  );
}

export function TicketBearbeiten() {
  const { id = "" } = useParams();
  const liste = useQuery({ queryKey: ["platform_support_tickets"], queryFn: async () => parse<TicketZeile[]>(await api.read("platform_support_tickets", { p_days: 365 })) });
  const verlauf = useQuery({ queryKey: ["support_ticket_platform", id], queryFn: async () => {
    const [m, e] = await Promise.all([
      api.select<TicketNachricht & { draft?: boolean }>("support_ticket_messages", { filters: [["ticket_id", "eq", id]], order: [["created_at", "asc"]] }),
      api.select<TicketEreignis>("support_ticket_events", { filters: [["ticket_id", "eq", id]], order: [["at", "asc"]] }),
    ]);
    return { m, e };
  } });
  const vorlagen = useQuery({ queryKey: ["support_reply_templates"], queryFn: () => api.select<{ id: string; title: string; body: string }>("support_reply_templates", { order: [["title", "asc"]] }) });
  const [text, setText] = useState("");
  const [intern, setIntern] = useState(false);
  const [neueVorlage, setNeueVorlage] = useState("");
  const { busy, error, run } = useAction();
  const x = liste.data?.find((t) => t.id === id);
  if (liste.error) return <Fehler error={liste.error} />;
  if (liste.isPending || verlauf.isPending) return <Laden />;
  if (!x) return <div className="panel tk-empty"><p>Ticket nicht gefunden.</p><Link className="btn" to="/betrieb/tickets">Zur Liste</Link></div>;
  const update = (p: Record<string, unknown>, ok: string) => run("upd", () => api.rpc("platform_support_update", { p_ticket: x.id, ...p }), ok);
  return (
    <>
      <title>{`${ticketNummer(x.number)} · Tickets · Plattform · CreatorOffice`}</title>
      <h1>{ticketNummer(x.number)} {x.subject}</h1>
      <div className="grid">
        <section className="panel stack">
          <h2>Ticket</h2>
          <p>
            {x.org_name} · <Link to={`/betrieb/agenturen/${x.org_id}`}>Agentur-Detail</Link><br />
            Von {x.created_by_email ?? "–"} · {kategorieLabel(x.category, tt)} · angelegt {date(x.created_at)}<br />
            {x.error_report_id && <Link to={`/betrieb/fehler/${x.error_report_id}`}>Zur Fehlermeldung (mit Spur und Bild)</Link>}
          </p>
          <label>Status<select value={x.status} disabled={!!busy} onChange={(e) => void update({ p_status: e.target.value }, "Status gespeichert.")}>
            {["offen", "in_bearbeitung", "wartet_auf_kunde", "geloest", "geschlossen"].map((s) => <option key={s} value={s}>{BETRIEB_STATUS[s]}</option>)}
          </select></label>
          <label>Priorität<select value={x.priority} disabled={!!busy} onChange={(e) => void update({ p_priority: e.target.value }, "Priorität gespeichert.")}>
            {["niedrig", "normal", "hoch", "dringend"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select></label>
          <p>Zugewiesen: {x.assignee_email ?? "niemand"}</p>
        </section>
        <section className="panel stack">
          <h2>Antwortvorlagen</h2>
          {(vorlagen.data ?? []).length === 0 ? <p>Noch keine Vorlagen.</p> : (
            <ul className="tk-liste-schlicht">{vorlagen.data!.map((v) => (
              <li key={v.id} className="row between"><span>{v.title}</span><button type="button" className="quiet" onClick={() => setText((s) => (s ? `${s}\n\n${v.body}` : v.body))}>Einfügen</button></li>
            ))}</ul>
          )}
          <label>Aktuelle Antwort als Vorlage speichern unter<input value={neueVorlage} onChange={(e) => setNeueVorlage(e.target.value)} placeholder="Titel der Vorlage" /></label>
          <button type="button" disabled={!!busy || neueVorlage.trim().length < 2 || text.trim().length < 2}
            onClick={() => void run("vorlage", () => api.insert("support_reply_templates", { title: neueVorlage.trim(), body: text.trim() }), "Vorlage gespeichert.").then(() => setNeueVorlage(""))}>Als Vorlage speichern</button>
        </section>
      </div>
      <section className="stack" aria-label="Verlauf">
        {(verlauf.data?.m ?? []).map((m: Nachricht) => (
          <article key={m.id} className={`panel stack tk-ticket-nachricht ${m.side === "support" ? "tk-ticket-support" : ""}`}>
            <small>{m.side === "support" ? (m.draft ? "Entwurf (noch nicht gesendet)" : m.internal ? "Interne Notiz" : "Support") : "Agentur"} · {date(m.created_at)} {time(m.created_at)}</small>
            <p className="tk-vorformatiert">{m.body}</p>
            {m.draft && (
              <div className="row">
                <button type="button" className="primary" disabled={!!busy}
                  onClick={() => void run(`frei-${m.id}`, () => api.rpc("platform_support_send_draft", { p_message: m.id }), "Entwurf freigegeben; die Agentur bekommt eine Mail.")}>Freigeben und senden</button>
                <button type="button" disabled={!!busy} onClick={() => setText(m.body)}>In das Antwortfeld übernehmen</button>
                <button type="button" className="quiet" disabled={!!busy}
                  onClick={() => void run(`weg-${m.id}`, () => api.rpc("platform_support_discard_draft", { p_message: m.id }), "Entwurf verworfen.")}>Verwerfen</button>
              </div>
            )}
            {m.attachments.length > 0 && <div className="row">{m.attachments.map((a) => <AnhangLink key={a.path} a={a} />)}</div>}
          </article>
        ))}
        <details><summary>Ereignisse ({verlauf.data?.e.length ?? 0})</summary>
          <ul className="tk-liste-schlicht">{(verlauf.data?.e ?? []).map((e) => <li key={e.id}><small>{date(e.at)} {time(e.at)}</small> {e.kind}: {e.from_value ?? "–"} → {e.to_value ?? "–"}</li>)}</ul>
        </details>
      </section>
      <form className="panel stack" onSubmit={(e) => {
        e.preventDefault();
        void run("antwort", () => api.rpc("platform_support_reply", { p_ticket: x.id, p_body: text, p_internal: intern }),
          intern ? "Interne Notiz gespeichert." : "Antwort gesendet; die Agentur bekommt eine Mail.").then((r) => { if (r !== undefined) { setText(""); setIntern(false); } });
      }}>
        <label>{intern ? "Interne Notiz (sieht die Agentur nicht)" : "Antwort an die Agentur"}<textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} /></label>
        <label className="row tk-check"><input type="checkbox" checked={intern} onChange={(e) => setIntern(e.target.checked)} /><span>Nur interne Notiz</span></label>
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div className="row">
          <button type="submit" className="primary" disabled={!!busy || !text.trim()}>{intern ? "Notiz speichern" : "Antwort senden"}</button>
          <Link className="btn" to="/betrieb/tickets">Zur Liste</Link>
        </div>
      </form>
    </>
  );
}

// ── KI-Protokoll (0177): Aktionen des MCP-Servers „creatoroffice“ ──────────────
interface ProtokollZeile { id: number; at: string; actor: string; tool: string; detail: Record<string, unknown>; ok: boolean; result: string | null }
export function KiProtokoll() {
  const q = useQuery({ queryKey: ["platform_operator_audit"], queryFn: async () => parse<ProtokollZeile[]>(await api.read("platform_operator_audit", { p_limit: 300 })) });
  return (
    <>
      <title>KI-Protokoll · Plattform · CreatorOffice</title>
      <h1>KI-Protokoll</h1>
      <p>Jede Abfrage und Aktion deines Claude über den Betreiber-Zugang (MCP „creatoroffice“): Werkzeug, Angaben ohne Inhalte, Ergebnis. Gelöscht nach 365 Tagen.</p>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : q.isPending ? <Laden /> : (q.data ?? []).length === 0 ? (
        <div className="panel tk-empty"><p>Noch keine Einträge.</p></div>
      ) : (
        <div className="tablewrap">
          <table>
            <caption className="sr-only">Aktionen über den Betreiber-Zugang</caption>
            <thead><tr><th scope="col">Zeit</th><th scope="col">Werkzeug</th><th scope="col">Angaben</th><th scope="col">Ergebnis</th></tr></thead>
            <tbody>
              {q.data!.map((r) => (
                <tr key={r.id}>
                  <td>{date(r.at)} {time(r.at)}</td>
                  <td>{r.tool}</td>
                  <td><code>{JSON.stringify(r.detail).slice(0, 160)}</code></td>
                  <td>{r.ok ? <span className="pill success"><span className="pill-zeichen">✓</span> {r.result ?? "ok"}</span> : <span className="pill error"><span className="pill-zeichen">!</span> {r.result ?? "Fehler"}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
